import Image from 'next/image';

import { Box, Heading, Paragraph, Text } from '@zillow/constellation-10';

import { POST_RENTAL_LISTING_ILLUSTRATION_URL } from '../../../../../constants/DigitalLibraryConstants';

import AddPropertyForm from '../../../AddPropertyModal/components/AddPropertyForm';

import type { FC } from 'react';
import type { UserSingleFamilyEnterpriseContractsResponseWebDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { UnitAlertValue } from '@zg-rentals/zillow-rental-manager/constants';
import type { ListingErrorsReducerState, ListingReducerProps } from '../../../AddPropertyModal/reducers';

type Props = {
  listing: ListingReducerProps;
  listingErrors: ListingErrorsReducerState;
  contractsResponse?: UserSingleFamilyEnterpriseContractsResponseWebDto;
  onListingChange: (keyName: keyof ListingReducerProps, newValue: string | boolean) => void;
  onCheckLocationRestriction: (
    fieldName?: keyof ListingReducerProps,
    fieldValue?: boolean | string,
  ) => Promise<true | void>;
  unitAlert: UnitAlertValue | null;
  propertyAlreadyPostedWarningMessage: string | Array<JSX.Element | string>;
  isStreetEasyRestricted?: boolean;
  isSubmitDisabled: boolean;
  setIsSubmitDisabled: (arg: boolean) => void;
  isRefocusingAddressInput: boolean;
  setIsRefocusingAddressInput: (arg: boolean) => void;
  questionNumber: number;
  questionTotal: number;
};

export const PropertyDetails: FC<Props> = ({
  listing,
  listingErrors,
  contractsResponse,
  onListingChange,
  onCheckLocationRestriction,
  unitAlert,
  propertyAlreadyPostedWarningMessage,
  isStreetEasyRestricted = false,
  isSubmitDisabled,
  setIsSubmitDisabled,
  isRefocusingAddressInput,
  setIsRefocusingAddressInput,
  questionNumber,
  questionTotal,
}) => {
  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: '500',
        alignItems: 'flex-start',
        width: '100%',
      }}
    >
      <Box aria-hidden="true">
        <Image src={POST_RENTAL_LISTING_ILLUSTRATION_URL} alt="" width={120} height={120} priority />
      </Box>
      <Box css={{ display: 'flex', flexDirection: 'column', gap: '200' }}>
        <Text textStyle="body-xs-bold" css={{ color: 'text.subtle' }}>
          Question {questionNumber} of {questionTotal}
        </Text>
        <Heading level={2} textStyle="heading-xs">
          Now, let’s add your property
        </Heading>
        <Paragraph>
          {isStreetEasyRestricted
            ? 'Start collecting applications and managing tenants. You’ll continue to list on StreetEasy.'
            : 'This basic information sets up the property in Zillow Rental Manager. Then you can create a rental listing for it.'}
        </Paragraph>
      </Box>
      <Box css={{ width: '100%' }}>
        <AddPropertyForm
          listing={{
            ...listing,
            isRoomForRent:
              typeof listing.isRoomForRent === 'boolean' ? listing.isRoomForRent : listing.isRoomForRent === 'true',
            isMultifamily:
              typeof listing.isMultifamily === 'boolean' ? listing.isMultifamily : listing.isMultifamily === 'true',
          }}
          contracts={contractsResponse}
          errorObj={listingErrors}
          onListingChange={onListingChange}
          onCheckLocationRestriction={onCheckLocationRestriction}
          unitAlert={unitAlert}
          propertyAlreadyPostedWarningMessage={propertyAlreadyPostedWarningMessage}
          isSubmitDisabled={isSubmitDisabled}
          setIsSubmitDisabled={setIsSubmitDisabled}
          behavior="Add"
          isRefocusingAddressInput={isRefocusingAddressInput}
          setIsRefocusingAddressInput={setIsRefocusingAddressInput}
          variant="embedded"
        />
      </Box>
    </Box>
  );
};
